import { useState, useRef, useEffect, useMemo } from 'react';
import { Link, Outlet, useRouter, useRouterState, Navigate } from '@tanstack/react-router';
import { partnerApi } from '@/lib/api-affiliate';
import { usePartnerLiveUpdates } from '@/lib/partner-sse';
import { useLiveConversion, useSseOnline } from '@/lib/live-events';
import {
  LayoutDashboard,
  Layers,
  Link as LinkIcon,
  TrendingUp,
  Wallet,
  Banknote,
  User,
  LogOut,
  ChevronDown,
  Menu,
  X,
  HelpCircle,
  Bell,
  Search,
  CircleDot,
  WifiOff,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, Tooltip } from './ui';
import { formatCents } from '@/lib/format';
import { CmdK } from './CmdK';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

const NAV: readonly NavItem[] = [
  { to: '/', label: 'Дашборд', icon: LayoutDashboard },
  { to: '/offers', label: 'Офферы', icon: Layers },
  { to: '/links', label: 'Ссылки', icon: LinkIcon },
  { to: '/conversions', label: 'Конверсии', icon: TrendingUp },
  { to: '/balance', label: 'Баланс', icon: Wallet },
  { to: '/payouts', label: 'Выплаты', icon: Banknote },
  { to: '/profile', label: 'Профиль', icon: User },
] as const;

const SIDEBAR_WIDTH = 240;
const TOPBAR_HEIGHT = 56;

function isRouteActive(currentPath: string, to: string): boolean {
  if (to === '/') return currentPath === '/';
  return currentPath === to || currentPath.startsWith(to + '/');
}

function statusBadge(status: string | undefined) {
  switch (status) {
    case 'approved':
    case 'active':
      return <Badge tone="success" dot>Активен</Badge>;
    case 'pending':
    case 'pending_review':
      return <Badge tone="warning" dot>На модерации</Badge>;
    case 'blocked':
    case 'rejected':
      return <Badge tone="danger" dot>Заблокирован</Badge>;
    default:
      return null;
  }
}

interface SidebarBodyProps {
  currentPath: string;
  onNavigate?: () => void;
  onClose?: () => void;
}

function SidebarBody({ currentPath, onNavigate, onClose }: SidebarBodyProps) {
  return (
    <div className="flex h-full flex-col">
      {/* Header — logo + brand */}
      <div className="flex items-center gap-2 px-4 border-b border-[var(--border)] h-14 shrink-0">
        <Link
          to="/"
          onClick={onNavigate}
          className="flex items-center gap-3 hover:opacity-90 transition-opacity"
          aria-label="Nexus Partner — главная"
        >
          <span
            className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)] bg-gradient-to-br from-[var(--accent)] to-[var(--accent-dim)] text-[var(--accent-fg)] font-bold text-sm"
            style={{ boxShadow: '0 4px 14px -4px var(--accent-glow)' }}
          >
            N
          </span>
          <span className="flex flex-col leading-tight">
            <span className="text-[15px] font-bold tracking-tight text-[var(--text)]">
              Nexus
            </span>
            <span className="text-[10px] uppercase tracking-[0.14em] text-[var(--accent-bright)] font-medium">
              Partner
            </span>
          </span>
        </Link>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрыть меню"
            className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-[var(--radius-xs)] text-[var(--text-quiet)] hover:text-[var(--text)] hover:bg-[var(--bg-elevated)] transition-colors"
          >
            <X size={15} />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3" aria-label="Главная навигация">
        <ul className="space-y-0.5 px-2.5">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active = isRouteActive(currentPath, item.to);
            return (
              <li key={item.to}>
                <Link
                  to={item.to as '/'}
                  onClick={onNavigate}
                  className={
                    'group relative flex items-center gap-2.5 rounded-[var(--radius-sm)] px-2.5 py-1.5 text-[13px] font-medium transition-colors duration-[var(--duration-fast)] ' +
                    (active
                      ? 'bg-[var(--accent-soft)] text-[var(--accent-bright)]'
                      : 'text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text)]')
                  }
                >
                  <span className="relative flex shrink-0 items-center justify-center">
                    <Icon size={15} strokeWidth={2} />
                  </span>
                  <span className="flex-1 truncate">{item.label}</span>
                  {active && (
                    <span
                      aria-hidden
                      className="absolute left-0 top-1/2 -translate-y-1/2 rounded-r bg-[var(--accent)] h-4 w-[3px]"
                    />
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Footer */}
      <div className="border-t border-[var(--border)] py-2 px-2.5 shrink-0">
        <a
          href="https://docs.nexus-crm.local"
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-2.5 rounded-[var(--radius-sm)] px-2.5 py-1.5 text-[13px] font-medium text-[var(--text-muted)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text)] transition-colors"
        >
          <HelpCircle size={15} />
          <span>Помощь</span>
        </a>
        <div className="px-2.5 pt-2 text-[10px] uppercase tracking-[0.14em] text-[var(--text-quiet)]">
          v1.0
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Header notification bell — count = how many partner:conversion events
//  fired since the bell was last clicked. Recent ones flash a dot.
// ─────────────────────────────────────────────────────────────────────────

function NotificationBell() {
  const { tick, last } = useLiveConversion();
  const [unread, setUnread] = useState(0);
  const [bumping, setBumping] = useState(false);
  const last100 = useRef<number[]>([]);

  useEffect(() => {
    if (tick === 0) return;
    setUnread((u) => Math.min(99, u + 1));
    setBumping(true);
    last100.current.push(Date.now());
    const t = window.setTimeout(() => setBumping(false), 700);
    return () => window.clearTimeout(t);
  }, [tick]);

  return (
    <Tooltip
      content={
        unread > 0
          ? `${unread} новых событий${last?.amountCents ? ` · последняя +${formatCents(last.amountCents)}` : ''}`
          : 'Нет новых уведомлений'
      }
    >
      <button
        type="button"
        aria-label={`Уведомления${unread > 0 ? `, ${unread} новых` : ''}`}
        onClick={() => setUnread(0)}
        className="relative inline-flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text)] motion-base"
        style={bumping ? { animation: 'bell-bump 0.7s var(--ease-spring)' } : undefined}
      >
        <Bell size={15} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-bold tabular-nums grid place-items-center"
            style={{
              backgroundColor: 'var(--danger)',
              color: 'white',
              boxShadow: '0 0 0 2px var(--bg-base), 0 0 10px color-mix(in oklch, var(--danger) 60%, transparent)',
            }}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
        <style>{`
          @keyframes bell-bump {
            0%   { transform: rotate(0deg); }
            25%  { transform: rotate(-12deg); }
            50%  { transform: rotate(10deg); }
            75%  { transform: rotate(-6deg); }
            100% { transform: rotate(0deg); }
          }
        `}</style>
      </button>
    </Tooltip>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Live $ pill — tiny tape running in the topbar that counts up as
//  conversions arrive. Resets at local midnight.
// ─────────────────────────────────────────────────────────────────────────

function LiveTodayPill() {
  const { tick, last } = useLiveConversion();
  const [cents, setCents] = useState(0);
  useEffect(() => {
    if (tick === 0 || !last) return;
    setCents((c) => c + last.amountCents);
  }, [tick, last]);

  // Reset daily — naive: reset if local date changes since mount.
  const mountDay = useMemo(() => new Date().toDateString(), []);
  useEffect(() => {
    const id = window.setInterval(() => {
      if (new Date().toDateString() !== mountDay) setCents(0);
    }, 60_000);
    return () => window.clearInterval(id);
  }, [mountDay]);

  if (cents === 0) return null;
  return (
    <Tooltip content="Сумма live-конверсий за сегодня (с момента открытия вкладки)">
      <span
        className="inline-flex items-center gap-1.5 h-7 px-2 rounded-full text-[11px] font-medium tabular-nums cursor-help"
        style={{
          backgroundColor: 'color-mix(in oklch, var(--success) 12%, transparent)',
          color: 'var(--success)',
          border: '1px solid color-mix(in oklch, var(--success) 28%, transparent)',
        }}
      >
        <CircleDot size={11} strokeWidth={2.5} className="animate-pulse" />
        +{formatCents(cents)} сегодня
      </span>
    </Tooltip>
  );
}

export function MainLayout() {
  const router = useRouter();
  // Реактивная подписка на путь — иначе хедер/сайдбар застревают на первом маршруте.
  const currentPath = useRouterState({ select: (s) => s.location.pathname });
  const me = partnerApi.me.useGet();
  usePartnerLiveUpdates(!!me.data && !me.isError);
  const sseOnline = useSseOnline();
  const logout = partnerApi.auth.useLogout();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cmdkOpen, setCmdkOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Cmd+K / Ctrl+K — toggle command palette globally.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCmdkOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  // Lock body scroll + Esc to close mobile drawer.
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [mobileOpen]);

  const handleLogout = async () => {
    setMenuOpen(false);
    try {
      await logout.mutateAsync();
      toast.success('Вы вышли из аккаунта');
      router.navigate({ to: '/login' });
    } catch {
      /* toast handled globally */
    }
  };

  if (me.isLoading) {
    return (
      <div className="min-h-screen grid place-items-center text-[var(--text-muted)] auth-bg">
        <div className="flex items-center gap-3">
          <div className="w-4 h-4 rounded-full border-2 border-[var(--accent)] border-t-transparent animate-spin" />
          <span className="text-sm">Загрузка…</span>
        </div>
      </div>
    );
  }

  if (me.isError) {
    const status = (me.error as { status?: number } | null)?.status;
    if (status === 401 || status === 403) {
      return <Navigate to="/login" />;
    }
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--text-muted)] p-6 text-center">
        Не удалось загрузить профиль:{' '}
        {me.error instanceof Error ? me.error.message : 'неизвестная ошибка'}
      </div>
    );
  }

  if (!me.data) return <Navigate to="/login" />;

  const displayName =
    me.data.displayName?.trim() ||
    (me.data.email ? me.data.email.split('@')[0] : 'partner');
  const initials = displayName.slice(0, 2).toUpperCase();

  const activeItem = NAV.find((n) => isRouteActive(currentPath, n.to));
  const pageTitle = activeItem?.label ?? 'Страница';
  const accountStatus = statusBadge(me.data.status);

  return (
    <div className="min-h-screen">
      {/* Desktop sidebar — fixed left */}
      <aside
        className="hidden md:flex fixed inset-y-0 left-0 z-30 bg-[var(--bg-sidebar)] border-r border-[var(--border)]"
        style={{ width: SIDEBAR_WIDTH }}
        aria-label="Главная навигация"
      >
        <div className="w-full">
          <SidebarBody currentPath={currentPath} />
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <button
            type="button"
            aria-label="Закрыть меню"
            onClick={() => setMobileOpen(false)}
            className="absolute inset-0 bg-[var(--bg-overlay)] backdrop-blur-sm"
            style={{ animation: 'partnerFadeIn var(--duration-fast) var(--ease-out)' }}
          />
          <aside
            role="dialog"
            aria-modal="true"
            aria-label="Главная навигация"
            className="absolute left-0 top-0 h-full bg-[var(--bg-sidebar)] border-r border-[var(--border)] shadow-[var(--shadow-lg)]"
            style={{
              width: SIDEBAR_WIDTH,
              animation: 'partnerSlideInLeft var(--duration-base) var(--ease-out)',
            }}
          >
            <SidebarBody
              currentPath={currentPath}
              onNavigate={() => setMobileOpen(false)}
              onClose={() => setMobileOpen(false)}
            />
          </aside>
          <style>{`
            @keyframes partnerSlideInLeft {
              from { transform: translateX(-100%); }
              to { transform: translateX(0); }
            }
            @keyframes partnerFadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
          `}</style>
        </div>
      )}

      {/* Right side — topbar + content */}
      <div className="flex min-h-screen flex-col md:pl-[240px]">
        <header
          className="sticky top-0 z-20 flex items-center gap-3 px-4 md:px-6 border-b border-[var(--border)] backdrop-blur-xl"
          style={{
            height: TOPBAR_HEIGHT,
            background: 'linear-gradient(to bottom, color-mix(in oklch, var(--bg-base) 86%, transparent), color-mix(in oklch, var(--bg-base) 72%, transparent))',
          }}
        >
          {/* Left: hamburger (mobile) + breadcrumb */}
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              aria-label="Открыть меню"
              className="md:hidden inline-flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text)] transition-colors"
            >
              <Menu size={16} />
            </button>
            <nav
              aria-label="Хлебные крошки"
              className="flex min-w-0 items-center gap-2 text-[13px]"
            >
              <span className="truncate font-medium text-[var(--text-muted)] hidden md:inline">
                Partner
              </span>
              <span className="text-[var(--text-quiet)] hidden md:inline" aria-hidden="true">
                /
              </span>
              <span className="truncate font-semibold text-[var(--text)]">
                {pageTitle}
              </span>
              {!sseOnline && (
                <Tooltip content="Live-обновления временно недоступны. Идёт переподключение.">
                  <span
                    className="ml-1 inline-flex items-center gap-1 px-1.5 h-5 rounded-full text-[10px] font-medium cursor-help"
                    style={{
                      backgroundColor: 'color-mix(in oklch, var(--warning) 14%, transparent)',
                      color: 'var(--warning)',
                      border: '1px solid color-mix(in oklch, var(--warning) 28%, transparent)',
                    }}
                  >
                    <WifiOff size={10} strokeWidth={2.5} />
                    offline
                  </span>
                </Tooltip>
              )}
            </nav>
          </div>

          {/* Center: live $ pill + account status */}
          <div className="hidden md:flex items-center gap-2 shrink-0">
            <LiveTodayPill />
            {accountStatus}
          </div>

          {/* Right cluster: Cmd-K + bell + avatar */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setCmdkOpen(true)}
              aria-label="Открыть командную панель (Cmd+K)"
              className="hidden sm:inline-flex items-center gap-2 h-8 px-2.5 rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--bg-elevated)]/40 text-[var(--text-muted)] hover:text-[var(--text)] hover:bg-[var(--bg-elevated)] hover:border-[var(--border-strong)] motion-base text-[12px]"
            >
              <Search size={13} />
              <span className="hidden md:inline">Поиск…</span>
              <kbd className="hidden md:inline text-[10px] border border-[var(--border)] rounded px-1 py-0.5 text-[var(--text-quiet)] bg-[var(--bg-base)]">
                ⌘K
              </kbd>
            </button>
            <NotificationBell />
          </div>

          {/* Right: avatar dropdown */}
          <div className="relative shrink-0" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="flex items-center gap-2 h-9 pl-1 pr-2 rounded-full border border-[var(--border)] hover:border-[var(--border-strong)] hover:bg-[var(--bg-elevated)] transition-colors"
            >
              <span className="w-7 h-7 rounded-full bg-[var(--accent-soft)] border border-[var(--accent)]/30 grid place-items-center text-[var(--accent-bright)] text-xs font-semibold">
                {initials}
              </span>
              <span className="text-sm text-[var(--text)] hidden sm:inline max-w-[140px] truncate">
                {displayName}
              </span>
              <ChevronDown size={14} className="text-[var(--text-muted)]" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-11 w-60 bg-[var(--bg-raised)] border border-[var(--border-strong)] rounded-[var(--radius-md)] shadow-[var(--shadow-lg)] py-1.5 page-fade-in">
                <div className="px-4 py-3 border-b border-[var(--border)]">
                  <div className="text-sm font-medium text-[var(--text)] truncate">
                    {displayName}
                  </div>
                  <div className="text-xs text-[var(--text-muted)] truncate">
                    {me.data.email}
                  </div>
                  <div className="mt-2">{accountStatus}</div>
                </div>
                <Link
                  to={'/profile' as '/'}
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2.5 px-4 h-9 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text)] transition-colors"
                >
                  <User size={14} /> Профиль
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2.5 px-4 h-9 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--danger)] transition-colors w-full text-left"
                >
                  <LogOut size={14} /> Выйти
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="flex-1">
          <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-6 pb-20 md:pb-6 page-fade-in">
            <Outlet />
          </div>
        </main>

        <footer className="hidden md:block border-t border-[var(--border)] py-4 text-center text-xs text-[var(--text-quiet)]">
          © {new Date().getFullYear()} Nexus CRM — Partner cabinet
        </footer>
      </div>

      {/* Mobile bottom-nav (< 768px) */}
      <nav
        aria-label="Нижняя навигация"
        className="md:hidden fixed bottom-0 inset-x-0 z-30 border-t border-[var(--border)] bg-[var(--bg-base)]/95 backdrop-blur-xl"
      >
        <ul className="grid grid-cols-5">
          {NAV.slice(0, 5).map((item) => {
            const Icon = item.icon;
            const active = isRouteActive(currentPath, item.to);
            return (
              <li key={item.to}>
                <Link
                  to={item.to as '/'}
                  className={
                    'flex flex-col items-center justify-center gap-0.5 h-14 text-[10px] font-medium ' +
                    (active
                      ? 'text-[var(--accent-bright)]'
                      : 'text-[var(--text-muted)] hover:text-[var(--text)]')
                  }
                >
                  <Icon size={18} strokeWidth={active ? 2.5 : 2} />
                  <span className="truncate max-w-full px-1">{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <CmdK open={cmdkOpen} onClose={() => setCmdkOpen(false)} />
    </div>
  );
}
